/**
 * UI language (Arabic / English) for this app only — static interface text.
 * Lives here, not in libs/shared, because libs/shared is copied from the backend
 * by `npm run libs:sync` and would overwrite it.
 */
import type { OrgSocialPlatform } from '@re/shared';

export type UiLanguage = 'ar' | 'en';
export const UI_LANGUAGE_KEY = 'org.uiLanguage';

/** UI language is intentionally client-local until the API supports preferences. */
export function getUiLanguage(): UiLanguage {
  try {
    const saved = typeof localStorage !== 'undefined' ? localStorage.getItem(UI_LANGUAGE_KEY) : null;
    if (saved === 'ar' || saved === 'en') return saved;
  } catch {
    /* ignore unavailable storage */
  }
  return typeof document !== 'undefined' && document.documentElement.lang === 'en' ? 'en' : 'ar';
}

export function setUiLanguage(language: UiLanguage) {
  try {
    localStorage.setItem(UI_LANGUAGE_KEY, language);
  } catch {
    /* ignore unavailable storage */
  }
  if (typeof document !== 'undefined') {
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }
}

export const tr = (arabic: string, english: string) => (getUiLanguage() === 'en' ? english : arabic);
export const uiLocale = () => (getUiLanguage() === 'en' ? 'en-US' : 'ar-JO-u-nu-latn');
export const uiDirection = () => (getUiLanguage() === 'en' ? 'ltr' : 'rtl') as 'ltr' | 'rtl';

type LocalizedOrganization = {
  name: string;
  nameAr?: string | null;
  name_ar?: string | null;
  nameEn?: string | null;
  name_en?: string | null;
};

/** Organization APIs may expose localized names in camelCase or snake_case. */
export function localizedOrganizationName(organization?: LocalizedOrganization | null): string {
  if (!organization) return '';
  if (getUiLanguage() === 'en') return organization.nameEn || organization.name_en || organization.name;
  return organization.nameAr || organization.name_ar || organization.name;
}

export const ORG_SOCIAL_PLATFORM_LABELS_EN: Record<OrgSocialPlatform, string> = {
  website: 'Website',
  instagram: 'Instagram',
  facebook: 'Facebook',
  twitter: 'X (Twitter)',
  tiktok: 'TikTok',
};
